import {
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useState,
} from 'react';

import { TextInputEvent } from '@design-system/types';
import { TextInput, TextInputProps } from '@design-system/ui';

// import { TextInputProps } from './TextInput';

interface IAutocompleteInputProps
  extends Omit<TextInputProps, 'inputMode' | 'defaultValue'> {
  readonly onSuggestionClick: (value: unknown) => void;
  readonly suggestions: ReadonlyArray<{
    readonly value: unknown;
    readonly label: string;
  }>;
}

export const AutocompleteInput = forwardRef<
  HTMLInputElement,
  IAutocompleteInputProps
>(function AutocompleteInput(props, ref) {
  const {
    suggestions,
    onSuggestionClick,
    onKeyDown,
    onBlur,
    onFocus,
    ...inputProps
  } = props;
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  const onKeyDownHandler = useCallback(
    (event: TextInputEvent<KeyboardEvent<HTMLInputElement>>) => {
      if (event.key?.toLowerCase() === 'escape') {
        setShowSuggestions(false);
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [onKeyDown],
  );

  const onSuggestionClickHander = useCallback(
    (value: unknown) => {
      onSuggestionClick(value);
      setShowSuggestions(false);
    },
    [onSuggestionClick],
  );

  const onBlurHandler = useCallback(
    (event: TextInputEvent<FocusEvent<HTMLInputElement>>) => {
      if (!event.relatedTarget) {
        // Delay hiding the suggestions to allow possible click in one of the suggestions
        setTimeout(() => {
          setShowSuggestions(false);
        }, 150);
      }

      if (onBlur) {
        onBlur(event);
      }
    },
    [onBlur],
  );

  const onFocusHandler = useCallback(
    (event: TextInputEvent<FocusEvent<HTMLInputElement>>) => {
      setShowSuggestions(true);

      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus],
  );

  return (
    <div className="relative">
      <TextInput
        {...inputProps}
        ref={ref}
        onKeyDown={onKeyDownHandler}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-primary rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((s, index) => (
            <li key={`${index}_${s.label}`}>
              <button
                type="button"
                onClick={() => onSuggestionClickHander(s.value)}
                className="w-full text-left text-base px-4 py-3 hover:bg-secondary focus:bg-secondary focus:outline-none transition-colors"
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
